import NotificationRecord, {
  type Notification,
} from '@packages/systems/core/records/NotificationRecord';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {WORKSPACE_ROLES} from '@packages/domains/authorization/permissions/catalog';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {createSelector} from 'reselect';

export const CLIENT_SEAT_AI_DISABLED_TOOLTIP =
  'AI features aren’t available through client access.';

const CLIENT_SEAT_AI_BLOCKED_NOTIFICATION_ID = 'client-seat-ai-blocked';

const selectWorkspaceCopilotEnabled = (state: DesignerStores): boolean =>
  state.SiteDataStore.workspaceCopilotEnabled;

const stripUserIdPrefix = (userId: string): string =>
  userId
    .replace(/^Person_/, '')
    .replace(/^Collaborator_/, '')
    .replace(/^Flow_/, '');

const selectSafeCurrentUserWorkspaceRole = createSelector(
  (state: DesignerStores) => state.SiteDataStore.userId,
  (state: DesignerStores) => state.UserStore.siteUsers,
  (userId, siteUsers): string | null => {
    if (!userId) {
      return null;
    }

    return siteUsers?.get(stripUserIdPrefix(userId))?.roles?.workspace || null;
  }
);

// Plain selector so non-React callers (e.g. the keyboard-shortcut handler in
// ShortcutHelper, which reads `flux.state` directly) can enforce the same
// client-seat AI block as the hook. This block is a UI-only convention — it is
// not encoded in the `canAccessAIFeatures` ability or the server's
// `restrictAIFeature` middleware — so every AI entry point must apply it.
export const selectIsClientSeatAiBlocked = createSelector(
  selectWorkspaceCopilotEnabled,
  selectSafeCurrentUserWorkspaceRole,
  (workspaceCopilotEnabled, workspaceRole): boolean =>
    workspaceCopilotEnabled && workspaceRole === WORKSPACE_ROLES.client
);

export function useIsClientSeatAiBlocked(): boolean {
  return useDesignerSelector(selectIsClientSeatAiBlocked);
}

export function buildClientSeatAiBlockedNotification(
  timestamp: number = Date.now()
): Notification {
  return NotificationRecord({
    id: CLIENT_SEAT_AI_BLOCKED_NOTIFICATION_ID,
    type: 'warning',
    message: CLIENT_SEAT_AI_DISABLED_TOOLTIP,
    dismissIcon: true,
    delay: 5000,
    timestamp,
  });
}
